import { filterPingPongMessages, PingWS } from "@cs124/pingpongws-client";
import base64url from "base64url";
import crypto from "crypto";
import queryString from "query-string";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import {
  ArriveLocation,
  ConnectionQuery,
  DepartLocation,
  HelpRequest,
  KillClient,
  RequestHelp,
  ServerMessages,
  StaffPresent,
  StatusUpdate,
  StudentArriveLocation,
  StudentDepartLocation,
  WithdrawRequest
} from "./types";
export const HelpableContext = createContext({
  available: false,
  connected: false
});
const generateRandomID = () => base64url.fromBase64(crypto.randomBytes(20).toString("base64"));
export const HelpableProvider = ({
  server,
  semester,
  number,
  loggedIn,
  staff,
  email,
  impersonating,
  children
}) => {
  const [connected, setConnected] = useState(false);
  const _connected = useRef(false);
  const disconnectedTime = useRef(void 0);
  const _impersonating = useRef(void 0);
  const client = useRef(
    typeof window !== "undefined" && sessionStorage.getItem("helpable:id") || generateRandomID()
  );
  const connection = useRef(void 0);
  const [request, setRequest] = useState();
  const [help, setHelp] = useState();
  const [status, setStatus] = useState();
  const [location, setLocation] = useState(
    typeof window !== "undefined" ? sessionStorage.getItem("helpable:location") : void 0
  );
  useEffect(() => {
    sessionStorage.setItem("helpable:id", client.current);
  }, []);
  const parameters = useMemo(() => {
    if (!loggedIn) {
      return void 0;
    }
    return ConnectionQuery.check({
      client: client.current,
      semester,
      number,
      headerVersion: "2025.1.0",
      ...impersonating && { impersonating }
    });
  }, [loggedIn, semester, number, impersonating]);
  useEffect(() => {
    if (!parameters) {
      connection.current?.close();
      return;
    }
    if (_connected.current && impersonating === _impersonating.current) {
      return;
    }
    connection.current?.close();
    const wsServer = server.replace("http", "ws");
    connection.current = PingWS(
      new ReconnectingWebSocket(`${wsServer}?${queryString.stringify(parameters)}`, [], {
        startClosed: true
      }),
      { interval: 32 * 1024, timeout: 16 * 1024, useOtherMessages: true }
    );
    connection.current.addEventListener("close", () => {
      setConnected(false);
      disconnectedTime.current = (/* @__PURE__ */ new Date()).valueOf();
      setStatus(void 0);
      _connected.current = false;
    });
    connection.current.addEventListener(
      "message",
      filterPingPongMessages(({ data }) => {
        const response = JSON.parse(data);
        if (!ServerMessages.guard(response)) {
          console.error(`Bad message: ${JSON.stringify(response, null, 2)}`);
          return;
        }
        _connected.current = true;
        _impersonating.current = impersonating;
        setConnected(true);
        if (StatusUpdate.guard(response)) {
          setStatus(response);
        } else if (KillClient.guard(response)) {
          const { client: c } = response;
          if (c === client.current) {
            setRequest(void 0);
            setHelp(void 0);
            sessionStorage.removeItem("helpable:staffpresent");
          }
        }
      })
    );
    connection.current.reconnect();
  }, [server, parameters, impersonating]);
  const staffPresent = useCallback(
    (present2, kind, reason) => {
      if (!staff) {
        return;
      }
      present2 ? sessionStorage.setItem("helpable:staffpresent", JSON.stringify(present2)) : sessionStorage.removeItem("helpable:staffpresent");
      connection.current?.send(
        JSON.stringify(StaffPresent.check({ type: "staffpresent", present: present2, kind, reason, client: true }))
      );
    },
    [staff]
  );
  const wasPresent = useRef(false);
  useEffect(() => {
    if (!connected || !staff) {
      return;
    }
    if (disconnectedTime.current) {
      const reconnectedTime = (/* @__PURE__ */ new Date()).valueOf() - disconnectedTime.current;
      if (reconnectedTime > 5 * 60 * 1024) {
        staffPresent(false, "connection", "long disconnection");
        return;
      }
    }
    const savedPresent = sessionStorage.getItem("helpable:staffpresent");
    savedPresent && staffPresent(true, "connection", "connection restored");
  }, [connected, staff, staffPresent]);
  const present = useMemo(() => {
    if (!status || !connected || !staff || !loggedIn || !(email || impersonating)) {
      return false;
    }
    const value = status.presentClients.includes(client.current);
    const myID = impersonating ? `${impersonating}@illinois.edu` : email;
    const otherClient = status.presentStaff.includes(myID);
    if (wasPresent.current && !value && otherClient) {
      sessionStorage.removeItem("helpable:staffpresent");
    }
    if (status.staffLockouts[myID]) {
      sessionStorage.removeItem("helpable:staffpresent");
    }
    wasPresent.current = value;
    return value;
  }, [status, connected, staff, loggedIn, email, impersonating]);
  const hadRequest = useRef(false);
  const requestHelp = useCallback((description, language, withWhat) => {
    const newRequest = RequestHelp.check({ type: "requesthelp", description, language, withWhat });
    hadRequest.current = true;
    setRequest(newRequest);
  }, []);
  const _withdrawReason = useRef("");
  const withdrawRequest = useCallback((reason) => {
    _withdrawReason.current = reason;
    setRequest(void 0);
  }, []);
  useEffect(() => {
    const savedRequest = sessionStorage.getItem("helpable:request");
    if (savedRequest) {
      hadRequest.current = true;
      setRequest(RequestHelp.check(JSON.parse(savedRequest)));
    }
  }, []);
  const reportRequest = useCallback((request2) => {
    request2 ? sessionStorage.setItem("helpable:request", JSON.stringify(request2)) : sessionStorage.removeItem("helpable:request");
    if (request2) {
      connection.current?.send(JSON.stringify(request2));
    } else if (hadRequest.current) {
      connection.current?.send(
        JSON.stringify(WithdrawRequest.check({ type: "withdrawrequest", reason: _withdrawReason.current }))
      );
    }
  }, []);
  useEffect(() => {
    reportRequest(request);
  }, [connected, request, reportRequest]);
  const startRequest = useCallback((h) => {
    setHelp(h);
  }, []);
  const _stopReason = useRef({ reason: "", backoffTime: -1 });
  const stopRequest = useCallback((reason, backoffTime = -1) => {
    _stopReason.current = { reason, backoffTime };
    setHelp(void 0);
  }, []);
  useEffect(() => {
    const savedHelp = sessionStorage.getItem("helpable:help");
    savedHelp && setHelp(JSON.parse(savedHelp));
  }, []);
  const previousHelpLocation = useRef(void 0);
  useEffect(() => {
    if (!staff) {
      return;
    }
    help ? sessionStorage.setItem("helpable:help", JSON.stringify(help)) : sessionStorage.removeItem("helpable:help");
    if (!connected) {
      return;
    }
    if (!help && !previousHelpLocation.current) {
      return;
    }
    connection.current.send(
      JSON.stringify(
        help ? ArriveLocation.check({ type: "arrivelocation", location: help.location }) : DepartLocation.check({ type: "departlocation", ..._stopReason.current })
      )
    );
    previousHelpLocation.current = help ? help.location : void 0;
  }, [connected, help, staff]);
  const withdrawTimer = useRef(void 0);
  useEffect(() => {
    if (!status) {
      return;
    }
    if (status.presentStaff.length === 0 && request) {
      clearTimeout(withdrawTimer.current);
      withdrawTimer.current = setTimeout(() => {
        setRequest(void 0);
      }, 8192);
    } else if (status.presentStaff.length > 0 || !request) {
      clearTimeout(withdrawTimer.current);
    }
  }, [status, request]);
  const killClient = useCallback(
    (client2) => {
      connected && connection.current.send(JSON.stringify(KillClient.check({ type: "killclient", client: client2 })));
      stopRequest("Client Killed");
    },
    [connected, stopRequest]
  );
  const arriveLocation = useCallback((location2) => {
    sessionStorage.setItem("helpable:location", location2);
    setLocation(location2);
  }, []);
  const leaveLocation = useCallback(() => {
    sessionStorage.removeItem("helpable:location");
    setLocation(void 0);
  }, []);
  const previousLocation = useRef(void 0);
  useEffect(() => {
    if (!connected) {
      return;
    }
    if (!location && !previousLocation.current) {
      return;
    }
    const message = location ? StudentArriveLocation.check({ type: "studentarrivelocation", location }) : StudentDepartLocation.check({ type: "studentdepartlocation" });
    previousLocation.current = location;
    connection.current.send(JSON.stringify(message));
  }, [connected, location]);
  useEffect(() => {
    return () => connection.current?.close();
  }, []);
  return /* @__PURE__ */ React.createElement(
    HelpableContext.Provider,
    {
      value: {
        available: true,
        connected,
        client: client.current,
        request,
        requestHelp,
        withdrawRequest,
        help,
        startRequest,
        stopRequest,
        present,
        staffPresent,
        status,
        killClient,
        location,
        arriveLocation,
        leaveLocation
      }
    },
    children
  );
};
export const useHelpable = () => {
  return useContext(HelpableContext);
};
export * from "./collections";
export * from "./types";
export { HelpRequest };
//# sourceMappingURL=index.js.map
